import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import useDeploymentScheduler from "./hooks/useDeploymentScheduler";
import {
  resetAddDeploymentScheduleData,
  resetUpdateDeploymentScheduleData,
} from "./redux/deploymentSchedulerSlice";
import { ApiStatus } from "../../common/services/api-status.enum";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifButton,
  MotifFormField,
  MotifInput,
  MotifDatePicker,
  MotifSelect,
  MotifOption,
  MotifToggleSwitch,
  MotifProgressLoader,
  MotifToast,
  MotifIcon,
} from "@ey-xd/motif-react";
import { closeIcon } from "../../common/MotifIcons";

import "./createDeploymentSchedule.scss";

const DateClearIcon = () => (
  <MotifIcon src={closeIcon} className="cds-date-clear-icon" />
);

const INITIAL_FORM = {
  deploymentName: "",
  startDate: null,
  startTime: "",
  startMeridiem: "AM",
  endDate: null,
  endTime: "",
  endMeridiem: "AM",
  enabled: true,
};

const parseDateTimeString = (dateTimeValue = "") => {
  const raw = String(dateTimeValue || "").trim();
  if (!raw) {
    return { date: null, time: "", meridiem: "AM" };
  }

  const [datePart, timePartRaw] = raw.split(",");
  const trimmedDatePart = (datePart || "").trim();
  let date = null;
  const dmyMatch = trimmedDatePart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmyMatch) {
    const day = Number(dmyMatch[1]);
    const month = Number(dmyMatch[2]) - 1;
    const year = Number(dmyMatch[3]);
    const parsed = new Date(year, month, day);
    if (!Number.isNaN(parsed.getTime())) date = parsed;
  } else if (trimmedDatePart) {
    const parsed = new Date(trimmedDatePart);
    if (!Number.isNaN(parsed.getTime())) date = parsed;
  }

  const timePart = (timePartRaw || "").trim();
  const match = timePart.match(/^(\d{1,2}):(\d{2})(?:\s*([AaPp][Mm]))?$/);
  if (!match) {
    return { date, time: "", meridiem: "AM" };
  }

  let hour = Number(match[1]);
  const minutes = match[2];
  let meridiem = (match[3] || "").toUpperCase();

  if (!meridiem) {
    meridiem = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;
  }

  return {
    date,
    time: `${String(hour).padStart(2, "0")}:${minutes}`,
    meridiem,
  };
};

const getFormFromSchedule = (schedule) => {
  if (!schedule) return INITIAL_FORM;

  const start = parseDateTimeString(schedule.startDateTime);
  const end = parseDateTimeString(schedule.endDateTime);

  return {
    deploymentName: schedule.deploymentName || "",
    startDate: start.date,
    startTime: start.time,
    startMeridiem: start.meridiem || "AM",
    endDate: end.date,
    endTime: end.time,
    endMeridiem: end.meridiem || "AM",
    enabled:
      typeof schedule.enabled === "boolean"
        ? schedule.enabled
        : String(schedule.status || "").toLowerCase() === "active",
  };
};

export const CreateDeploymentSchedule = ({
  show,
  onClose,
  onSave,
  mode = "create",
  scheduleId = "",
  initialData = null,
  lastUpdatedBy = "",
  lastUpdatedOn = "",
}) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [initialForm, setInitialForm] = useState(INITIAL_FORM);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const isEditMode = mode === "edit";
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const dispatch = useDispatch();
  const { addDeploymentScheduleData, updateDeploymentScheduleData } =
    useDeploymentScheduler();
  const submissionState = isEditMode
    ? updateDeploymentScheduleData
    : addDeploymentScheduleData;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const openCalendarFor = (containerRef) => {
    setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const picker = container.querySelector(".react-date-picker");
      if (picker && picker.classList.contains("react-date-picker--open")) return;
      const btn = container.querySelector(".react-date-picker__calendar-button");
      if (btn) btn.click();
    }, 0);
  };

  const todayMinDate = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const toStartOfDay = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const startDateOnly = React.useMemo(
    () => toStartOfDay(form.startDate),
    [form.startDate]
  );
  const endDateOnly = React.useMemo(
    () => toStartOfDay(form.endDate),
    [form.endDate]
  );

  const startPickerMaxDate = endDateOnly || undefined;

  const endPickerMinDate =
    startDateOnly && startDateOnly.getTime() > todayMinDate.getTime()
      ? startDateOnly
      : todayMinDate;

  const parseTimeToMinutes = (timeValue, meridiem) => {
    const trimmed = String(timeValue || "").trim();
    if (!trimmed) return null;
    const match = trimmed.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
    if (!match) return null;
    let hours = Number(match[1]);
    const minutesRaw = match[2];
    let minutes;
    if (minutesRaw && minutesRaw.length === 1) minutes = Number(minutesRaw) * 10;
    else minutes = Number(minutesRaw || 0);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return null;
    const m = String(meridiem || "").toUpperCase();
    if (m === "PM" && hours < 12) hours += 12;
    if (m === "AM" && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  const isSameDay =
    startDateOnly &&
    endDateOnly &&
    startDateOnly.getTime() === endDateOnly.getTime();
  const startMinutes = parseTimeToMinutes(form.startTime, form.startMeridiem);
  const endMinutes = parseTimeToMinutes(form.endTime, form.endMeridiem);
  const startTimeInvalid =
    String(form.startTime || "").trim() !== "" && startMinutes === null;
  const endTimeInvalid =
    String(form.endTime || "").trim() !== "" && endMinutes === null;
  const timeRangeInvalid =
    isSameDay &&
    startMinutes !== null &&
    endMinutes !== null &&
    startMinutes >= endMinutes;

  const formatDateDisplay = (value) => {
    if (!value) return "DD/MM/YYYY";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "DD/MM/YYYY";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear());
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    if (show) {
      const next = isEditMode ? getFormFromSchedule(initialData) : INITIAL_FORM;
      setForm(next);
      setInitialForm(next);
      setShowErrorToast(false);
      setShowSuccessToast(false);
      dispatch(
        isEditMode
          ? resetUpdateDeploymentScheduleData()
          : resetAddDeploymentScheduleData()
      );
    }
  }, [show, isEditMode, initialData, dispatch]);

  useEffect(() => {
    if (submissionState?.status === ApiStatus.ERROR) {
      setShowErrorToast(true);
      dispatch(
        isEditMode
          ? resetUpdateDeploymentScheduleData()
          : resetAddDeploymentScheduleData()
      );
    } else if (submissionState?.status === ApiStatus.SUCCESS) {
      setShowErrorToast(false);
      setShowSuccessToast(true);
      dispatch(
        isEditMode
          ? resetUpdateDeploymentScheduleData()
          : resetAddDeploymentScheduleData()
      );
      if (onCloseRef.current) {
        onCloseRef.current();
      }
    }
  }, [submissionState?.status, isEditMode, dispatch]);

  useEffect(() => {
    if (!showSuccessToast) return undefined;
    const timer = setTimeout(() => setShowSuccessToast(false), 7000);
    return () => clearTimeout(timer);
  }, [showSuccessToast]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const areFormsEqual = (a, b) => {
    const dateEqual = (x, y) => {
      const xt = x ? new Date(x).getTime() : null;
      const yt = y ? new Date(y).getTime() : null;
      return xt === yt;
    };
    return (
      String(a.deploymentName || "") === String(b.deploymentName || "") &&
      dateEqual(a.startDate, b.startDate) &&
      dateEqual(a.endDate, b.endDate) &&
      String(a.startTime || "") === String(b.startTime || "") &&
      String(a.endTime || "") === String(b.endTime || "") &&
      String(a.startMeridiem || "AM") === String(b.startMeridiem || "AM") &&
      String(a.endMeridiem || "AM") === String(b.endMeridiem || "AM") &&
      Boolean(a.enabled) === Boolean(b.enabled)
    );
  };

  const isFormDirty = !areFormsEqual(form, initialForm);

  const handleDateBoxMouseDown = (event) => {
    if (
      event.target.closest(".react-date-picker__calendar-button") ||
      event.target.closest(".react-date-picker__clear-button") ||
      event.target.closest(".react-date-picker__calendar") ||
      event.target.closest(".react-calendar")
    ) {
      return;
    }
    event.preventDefault();
    const calendarButton = event.currentTarget.querySelector(
      ".react-date-picker__calendar-button"
    );
    if (calendarButton) {
      calendarButton.click();
    }
  };

  const isSubmitting = submissionState?.status === ApiStatus.LOADING;

  const isSaveDisabled =
    isSubmitting ||
    !isFormDirty ||
    timeRangeInvalid ||
    startTimeInvalid ||
    endTimeInvalid ||
    !String(form.deploymentName || "").trim() ||
    !form.startDate ||
    !form.endDate ||
    !String(form.startTime || "").trim() ||
    !String(form.endTime || "").trim();

  const handleSave = () => {
    if (isSaveDisabled) return;
    setShowErrorToast(false);
    if (onSave) {
      onSave(form);
    }
    setInitialForm(form);
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  const toasts = (
    <>
      {showErrorToast && (
        <div className="cds-toast-container">
          <MotifToast
            variant="error"
            onClose={() => setShowErrorToast(false)}
          >
            <div className="cds-toast-title">Failed to schedule deployment</div>
            <div className="cds-toast-message">
              An API error occurred while scheduling the deployment. Please try
              again.
            </div>
          </MotifToast>
        </div>
      )}
      {showSuccessToast && (
        <div className="cds-toast-container">
          <MotifToast
            variant="success"
            onClose={() => setShowSuccessToast(false)}
          >
            <div className="cds-toast-title">
              Deployment scheduled successfully
            </div>
            <div className="cds-toast-message">
              The deployment window has been configured successfully.
            </div>
          </MotifToast>
        </div>
      )}
    </>
  );

  if (!show) {
    return toasts;
  }

  return (
    <>
      {toasts}
      <MotifModal
        show={show}
        onClose={handleCancel}
        size="md"
        className="create-deployment-schedule-modal"
        focusTrapOptions={{
          allowOutsideClick: true,
          clickOutsideDeactivates: true,
          fallbackFocus: () => document.body,
        }}
      >
        {isSubmitting && (
          <div className="cds-loader-overlay">
            <MotifProgressLoader show variant="default" />
          </div>
        )}
        <MotifModalHeader>
        {isEditMode ? `Edit Schedule: ${scheduleId}` : "Create New Schedule"}
      </MotifModalHeader>
      {isEditMode && (
        <div className="cds-header-meta">
          <div className="cds-footer-meta-item">
            Last Updated By: <span>{lastUpdatedBy || "-"}</span>
          </div>
          <div className="cds-footer-meta-item">
            Last Updated On: <span>{lastUpdatedOn || "-"}</span>
          </div>
        </div>
      )}
      <MotifModalBody>
        <div className="cds-form">
          <div className="cds-row cds-row-full">
            <div className="cds-field">
              <label className="cds-label" htmlFor="cds-deployment-name">
                Deployment Name
              </label>
              <MotifFormField>
                <MotifInput
                  id="cds-deployment-name"
                  value={form.deploymentName}
                  onChange={(e) =>
                    updateField("deploymentName", e.target?.value)
                  }
                  placeholder="Enter name"
                  autoComplete="off"
                  hideClearButton
                />
              </MotifFormField>
            </div>
          </div>

          <div className="cds-row cds-row-split">
            <div className="cds-col cds-col-date">
              <div className="cds-field">
                <label className="cds-label">Start Date</label>
                <div
                  className="cds-date-display"
                  data-display-date={formatDateDisplay(form.startDate)}
                  data-has-value={Boolean(form.startDate)}
                  onMouseDown={handleDateBoxMouseDown}
                  ref={startDateRef}
                >
                  <MotifDatePicker
                    format="dd/MM/y"
                    showLeadingZeros
                    dayPlaceholder="DD"
                    monthPlaceholder="MM"
                    yearPlaceholder="YYYY"
                    minDate={todayMinDate}
                    maxDate={startPickerMaxDate}
                    todayMark
                    clearIcon={form.startDate ? <DateClearIcon /> : null}
                    value={form.startDate}
                    onChange={(value) => {
                      updateField("startDate", value);
                      if (value === null) openCalendarFor(startDateRef);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="cds-col cds-col-time">
              <div
                className={`cds-field${
                  startTimeInvalid || timeRangeInvalid ? " cds-field-error" : ""
                }`}
              >
                <label className="cds-label" htmlFor="cds-start-time">
                  Start Time
                </label>
                <MotifFormField className="cds-time-field">
                  <MotifInput
                    id="cds-start-time"
                    value={form.startTime}
                    onChange={(e) => updateField("startTime", e.target?.value)}
                    placeholder="Enter time"
                    autoComplete="off"
                    hideClearButton
                  />
                  {form.startTime && (
                    <button
                      type="button"
                      aria-label="Clear start time"
                      className="cds-time-clear"
                      onClick={() => updateField("startTime", "")}
                    >
                      <MotifIcon src={closeIcon} />
                    </button>
                  )}
                </MotifFormField>
                {startTimeInvalid ? (
                  <div className="cds-error-message">
                    Please enter valid time.
                  </div>
                ) : timeRangeInvalid ? (
                  <div className="cds-error-message">
                    Start time must be earlier than end time.
                  </div>
                ) : null}
              </div>
            </div>
            <div className="cds-col cds-col-meridiem">
              <div className="cds-field">
                <label className="cds-label">&nbsp;</label>
                <MotifFormField>
                  <MotifSelect
                    value={form.startMeridiem}
                    onChange={(value) => updateField("startMeridiem", value)}
                    ariaLabelledBy="start-meridiem"
                  >
                    <MotifOption value="AM">AM</MotifOption>
                    <MotifOption value="PM">PM</MotifOption>
                  </MotifSelect>
                </MotifFormField>
              </div>
            </div>
          </div>

          <div className="cds-row cds-row-split">
            <div className="cds-col cds-col-date cds-col-date-end">
              <div className="cds-field">
                <label className="cds-label">End Date</label>
                <div
                  className="cds-date-display"
                  data-display-date={formatDateDisplay(form.endDate)}
                  data-has-value={Boolean(form.endDate)}
                  onMouseDown={handleDateBoxMouseDown}
                  ref={endDateRef}
                >
                  <MotifDatePicker
                    format="dd/MM/y"
                    showLeadingZeros
                    dayPlaceholder="DD"
                    monthPlaceholder="MM"
                    yearPlaceholder="YYYY"
                    minDate={endPickerMinDate}
                    todayMark
                    clearIcon={form.endDate ? <DateClearIcon /> : null}
                    value={form.endDate}
                    onChange={(value) => {
                      updateField("endDate", value);
                      if (value === null) openCalendarFor(endDateRef);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="cds-col cds-col-time">
              <div
                className={`cds-field${
                  endTimeInvalid || timeRangeInvalid ? " cds-field-error" : ""
                }`}
              >
                <label className="cds-label" htmlFor="cds-end-time">
                  End Time
                </label>
                <MotifFormField className="cds-time-field">
                  <MotifInput
                    id="cds-end-time"
                    value={form.endTime}
                    onChange={(e) => updateField("endTime", e.target?.value)}
                    placeholder="Enter time"
                    autoComplete="off"
                    hideClearButton
                  />
                  {form.endTime && (
                    <button
                      type="button"
                      aria-label="Clear end time"
                      className="cds-time-clear"
                      onClick={() => updateField("endTime", "")}
                    >
                      <MotifIcon src={closeIcon} />
                    </button>
                  )}
                </MotifFormField>
                {endTimeInvalid ? (
                  <div className="cds-error-message">
                    Please enter valid time.
                  </div>
                ) : timeRangeInvalid ? (
                  <div className="cds-error-message">
                    End time must be later than start time.
                  </div>
                ) : null}
              </div>
            </div>
            <div className="cds-col cds-col-meridiem">
              <div className="cds-field">
                <label className="cds-label">&nbsp;</label>
                <MotifFormField>
                  <MotifSelect
                    value={form.endMeridiem}
                    onChange={(value) => updateField("endMeridiem", value)}
                    ariaLabelledBy="end-meridiem"
                  >
                    <MotifOption value="AM">AM</MotifOption>
                    <MotifOption value="PM">PM</MotifOption>
                  </MotifSelect>
                </MotifFormField>
              </div>
            </div>
          </div>

          <div className="cds-toggle-row">
            <MotifToggleSwitch
              id="enable-deployment-window"
              checked={form.enabled}
              onChange={(e) =>
                updateField("enabled", e?.target?.checked ?? !form.enabled)
              }
              hideLabels
            />
            <div className="cds-toggle-text">
              <div className="cds-toggle-title">Enable Deployment Window</div>
              <div className="cds-toggle-subtitle">
                Activates the configured deployment schedule
              </div>
            </div>
          </div>
        </div>
      </MotifModalBody>
      <MotifModalFooter>
        <div className="cds-footer">
          <div className="cds-footer-actions">
            <MotifButton
              onClick={handleCancel}
              size="small"
              type="button"
              variant="secondary"
              className="cds-cancel-btn"
            >
              Cancel
            </MotifButton>
            <MotifButton
              onClick={handleSave}
              size="small"
              type="button"
              variant="primary"
              className="cds-save-btn"
              disabled={isSaveDisabled}
            >
              {isEditMode ? "Update" : "Save"}
            </MotifButton>
          </div>
        </div>
      </MotifModalFooter>
    </MotifModal>
    </>
  );
};

export default CreateDeploymentSchedule;
